import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ConceptStatusValidity, ImportMaterialClaim, MaterialArea, MaterialStatus, MaterialStatusQuery, ValidityDeterminate } from '@fusion-advantage/backcompat'
import { ConceptStatusValidityInfo, formShouldQueryMaterialStatus, IndexedModel, LabActionType, LabInputType, LabItem, LabRecommendationItem, timestamp, ValiditySeverity } from '@fusion-advantage/formlib'
import _fp from "lodash/fp"
import { FormRootState } from './formStore'
import { selectExport } from './selectForm'
import { MaterialStateStatus, materialStatusQueryKey } from './asyncResultSlice'

export interface LabsSliceState {
	models: LabItem[]
	recommendations: LabRecommendationItem[]
}

const initialState: LabsSliceState = {
	models: [],
	recommendations: [],
}

const labsSlice = createSlice({
	name: 'labs',
	initialState,
	reducers: {
		loadLabs(state, action: PayloadAction<LabsSliceState>) {
			state.models = action.payload.models
			state.recommendations = action.payload.recommendations
		},
		updateLabByIdx(state, action: PayloadAction<IndexedModel<Partial<LabItem>>>) {
			const { stateIdx, model } = action.payload
			if(model.action) {
				// action changed, and it's not null
				// if the value is empty, switch it to null action
				switch(model.action.type) {
					case LabActionType.Codes:
						if(model.action.code === '') {
							model.action = null
						} else {
							model.action.code = model.action.code.toUpperCase()
						}
						break
					case LabActionType.Check:
						if(!model.action.performed) {
							model.action = null
						}
						break
					case LabActionType.Decline:
						if(model.action.reason === '') {
							model.action = null
						}
						break
					case LabActionType.BMD:
						if(model.action.densityIndex === '' &&
							model.action.corticalBoneThickness === '' &&
							model.action.interpretation === ''
						) {
							model.action = null
						}
						break
				}
			}
			model.updatedat = timestamp()
			state.models[stateIdx] = _fp.merge(state.models[stateIdx], model)
		},
	},
})

export const { loadLabs, updateLabByIdx } = labsSlice.actions
export default labsSlice.reducer

export interface ParsedLabRecommendation {
	inputType: LabInputType
	declineReasons: string[]
	validationRegex: string|undefined
}

export type ParsedLabRecommendationByGuidGetter = (guid: string) => ParsedLabRecommendation

const selectGetParsedLabRecommendationByGuid = createSelector(
	(state: FormRootState) => state.labs.recommendations,
	(recommendations): ParsedLabRecommendationByGuidGetter => {
		let recoByGuid: Record<string, ParsedLabRecommendation> = {}
		if(recommendations) {
			recoByGuid = _fp.fromPairs(recommendations.map((reco, idx) => {
				let declineReasons: string[] = []
				if(reco.declinereasons && reco.declinereasons !== '') {
					try {
						const reasons2 = JSON.parse(reco.declinereasons)
						if(!_fp.isArray(reasons2)) {
							throw new Error('not array')
						}
						declineReasons = reasons2
					} catch(ex) {
						declineReasons = ['Lab decline reasons unparseable']
					}
				}
				const parsed: ParsedLabRecommendation = {
					inputType: reco.value_type ?? LabInputType.Unspecified,
					declineReasons,
					validationRegex: reco.validation_regex,
				}
				return [reco.lab_guid, parsed]
			}))
		}
		return (guid: string): ParsedLabRecommendation => {
			return recoByGuid[guid] ?? {
				inputType: LabInputType.Unspecified,
				declineReasons: [],
			}
		}
	}
)

export interface LabRow extends ParsedLabRecommendation {
	model: LabItem
	validity: ConceptStatusValidity
	inputValidity: ConceptStatusValidity|null
}

interface labRowValidatorArgs {
	models: LabItem[]
	getRecoByGuid: ParsedLabRecommendationByGuidGetter
	shouldQueryMaterialStatus: boolean
	asyncMaterialStatuses: {[queryKey: string]: MaterialStatus|MaterialStateStatus}
}

export const processLabRows = ({models, getRecoByGuid, shouldQueryMaterialStatus, asyncMaterialStatuses}: labRowValidatorArgs): LabRow[] => {
	const usedLabCodes: Record<string, boolean> = {}
	return models.map((model): LabRow => {
		const row = {
			model,
			...(getRecoByGuid(model.labguid)),
		}
		const simpleRow = (validity: ConceptStatusValidity): LabRow => ({
			...row,
			validity,
			inputValidity: validity,
		})
		// always ensure there are never dups in the same table
		if(model.action?.type === LabActionType.Codes) {
			if(usedLabCodes[model.action.code]) {
				return simpleRow({
					determinate: ValidityDeterminate.Invalid,
					message: `Lab kit code '${model.action.code}' entered multiple times`,
				})
			}
			usedLabCodes[model.action.code] = true
		}
		// required + no action: error
		if(model.required && !model.action) {
			switch(row.inputType) {
				case LabInputType.Codes:
					return {
						...row,
						validity: {
							determinate: ValidityDeterminate.Required,
							message: 'Labs are missing required information',
						},
						inputValidity: {
							determinate: ValidityDeterminate.Required,
							message: 'Lab kit code or decline reason is required',
						},
					}
				case LabInputType.Check:
					return {
						...row,
						validity: {
							determinate: ValidityDeterminate.Required,
							message: 'Labs are missing required information',
						},
						inputValidity: {
							determinate: ValidityDeterminate.Required,
							message: 'Lab performance or decline reason is required',
						},
					}
				case LabInputType.BMD:
					return {
						...row,
						validity: {
							determinate: ValidityDeterminate.Required,
							message: 'Labs are missing required information',
						},
						inputValidity: {
							determinate: ValidityDeterminate.Required,
							message: 'Bone mineral density information or decline reason is required',
						},
					}
				default:
					return {
						...row,
						validity: {
							determinate: ValidityDeterminate.Required,
							message: 'Some labs are required but no input type is available',
						},
						inputValidity: {
							determinate: ValidityDeterminate.Required,
							message: 'Lab is required but no input type is available',
						},
					}
			}
		}
		// not required + no action: ok
		// not querying material status during this codenext: ok
		if(!model.action || !shouldQueryMaterialStatus) {
			return {
				...row,
				validity: {
					determinate: ValidityDeterminate.Valid
				},
				inputValidity: null,
			}
		}
		// validate the action if code
		if(model.action.type === LabActionType.Codes) {
			const query: MaterialStatusQuery = {
				area: MaterialArea.Labs,
				id: model.action.code,
				type: model.labname,
			}
			const queryKey = materialStatusQueryKey(query)
			const status = asyncMaterialStatuses[queryKey] ?? undefined
			console.debug(`get asyncMaterialStatuses[${queryKey}] = ${status}`)
			// updateLabByIdx will set a null action if the field is empty
			// so by now we know that `.code` has some characters to test
			// if a regex is provided
			if(row.validationRegex) {
				const regex = new RegExp(row.validationRegex)
				if(regex.test(model.action.code) === false) {
					return simpleRow({
						determinate: ValidityDeterminate.Invalid,
						message: 'Lab kit entered is invalid',
					})
				}
			}
			switch(status) {
				case MaterialStateStatus.QueryPending:
					// should only be extremely brief,
					// otherwise it indicates a bug
					return simpleRow({
						determinate: ValidityDeterminate.Invalid,
						message: 'Lab material query did not complete; please contact support',
					})
				case MaterialStatus.Used:
					return simpleRow({
						determinate: ValidityDeterminate.Invalid,
						message: 'Lab kit has already been used',
					})
				case MaterialStatus.Expired:
					return simpleRow({
						determinate: ValidityDeterminate.Invalid,
						message: 'Lab kit is expired',
					})
				case MaterialStatus.Available:
					return {
						...row,
						validity: {
							determinate: ValidityDeterminate.Valid,
						},
						inputValidity: {
							determinate: ValidityDeterminate.Valid,
							message: 'Lab kit is available for use',
						},
					}
				case MaterialStatus.Unknown:
					return simpleRow({
						determinate: ValidityDeterminate.Abnormal,
						message: "Lab kit status is unknown",
					})
				case MaterialStatus.WrongType:
					return simpleRow({
						determinate: ValidityDeterminate.Invalid,
						message: 'Lab kit entered is of incorrect type',
					})
				case undefined:
					// not found in asyncResults (this should only be extremely brief,
					// otherwise it indicates a bug)
					return simpleRow({
						determinate: ValidityDeterminate.Invalid,
						message: 'Lab material not in async results; please contact support',
					})
				default:
					// bug
					return simpleRow({
						determinate: ValidityDeterminate.Invalid,
						message: 'Lab material status not in case statement; please contact support',
					})
			}
		}
		// validate the action if BMD
		if(model.action.type === LabActionType.BMD) {
			const { densityIndex, corticalBoneThickness, interpretation } = model.action
			// BMD rule # 1: when one field has a value, all bmd fields are required
			// by this point we know from updateLabByIdx that one of these
			// fields is filled in
			if(!densityIndex || !corticalBoneThickness || !interpretation) {
				return simpleRow({
					determinate: ValidityDeterminate.Invalid,
					message: 'Bone mineral density information is incomplete',
				})
			}
			const densityIndexParsed = parseInt(densityIndex)
			const boneThicknessParsed = parseInt(corticalBoneThickness)
			// density must be between -10 and 10
			if(Number.isNaN(densityIndexParsed) || densityIndexParsed < 0 || densityIndexParsed > 2) {
				return simpleRow({
					determinate: ValidityDeterminate.Invalid,
					message: 'Density index must be between 0 and 2',
				})
			}
			// thickness must be between -10 and 10
			if(Number.isNaN(boneThicknessParsed) || boneThicknessParsed < -10 || boneThicknessParsed > 10) {
				return simpleRow({
					determinate: ValidityDeterminate.Invalid,
					message: 'Cortical bone thickness must be between -10 and 10',
				})
			}
		}
		// for other input types, updateLabByIdx ensures enough
		// valid input to proceed
		return simpleRow({
			determinate: ValidityDeterminate.Valid
		})
	})
}

export const selectLabRows = createSelector(
	(state: FormRootState) => state.labs.models,
	selectGetParsedLabRecommendationByGuid,
	(state: FormRootState) => formShouldQueryMaterialStatus(selectExport(state)?.Form.Encounter.CodeNext),
	(state: FormRootState) => state.asyncResult.materialStatus,
	(models, getRecoByGuid, shouldQueryMaterialStatus, asyncMaterialStatuses): LabRow[] => {
		return processLabRows({models, getRecoByGuid, shouldQueryMaterialStatus, asyncMaterialStatuses})
	}
)

export const selectLabMaterialClaims = createSelector(
	selectLabRows,
	(rows) => {
		return rows
			.map(({model, validity}): ImportMaterialClaim|null => {
				const severity = new ConceptStatusValidityInfo(validity).severity
				if(severity === ValiditySeverity.Error) {
					return null
				}
				if(model.action?.type === LabActionType.Codes) {
					return {
						area: MaterialArea.Labs,
						id: model.action.code,
					}
				}
				return null
			})
			.filter(claim => claim !== null)
	}
)
